import { useQuery, useMutation } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import {
    EMAIL_ALL_QUERY,
    CONTACT_AGENTS
} from '@zoopla/listing-search-queries';
import {
    EmailAll,
    EmailAllForm,
    EmailAllFormField,
    FormValues
} from '../EmailAgents.types';
import { useForm } from 'react-hook-form';
import { triggerTracking } from '@zoopla/tracking';
import { trackEvent, Taxonomy } from '@zoopla/tracking';
import {
    SearchContext,
    AnalyticsTaxonomy
} from '@zoopla/listing-search-context';
import { snakeCaseKeys } from '@zoopla/tracking';

const defaultEmailAll = {
    form: null,
    result: null
};

const FIRST_FIELDSET = 0;
const SECOND_FIELDSET = 1;

export const requiredErrorMessages: { [k: string]: string } = {
    name: 'Please enter your name',
    email: 'Please enter your email address',
    phone: 'Please enter your phone number',
    status: 'Please select the option that best reflects your status',
    message: ''
};

export const patternErrorMessages: { [k: string]: string } = {
    name: 'Please enter both your first and last names e.g. John Smith',
    email: 'Please provide a full, valid email address e.g. johnsmith@gmail.com',
    phone: 'Please provide a full, valid phone number - include a + before international numbers',
    status: '',
    message: ''
};

export const getEmailFormField = (
    fieldName: string,
    location: EmailAll | null
) => {
    let field = location?.formConfig?.fields?.[
        FIRST_FIELDSET
    ]?.fieldset?.fields?.find(
        (currentField: EmailAllFormField) => currentField.name === fieldName
    );
    if (!field) {
        field = location?.formConfig?.fields?.[
            SECOND_FIELDSET
        ]?.fieldset?.fields?.find(
            (currentField: EmailAllFormField) => currentField.name === fieldName
        );
    }
    return field;
};

const useEmailAll = (
    path: string,
    eventCategory = 'search results',
    taxonomy?: AnalyticsTaxonomy
) => {
    const [isLoading, setLoading] = useState(false);
    const [isError, setFormError] = useState(false);
    const [emailAll, setEmailAll] = useState<EmailAllForm>(defaultEmailAll);
    const [abort, setAbort] = useState(false);
    const {
        state: { searchType }
    } = useContext(SearchContext);

    const formHandlers = useForm<FormValues>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        criteriaMode: 'all'
    });

    useEffect(() => {
        return () => {
            setAbort(true);
        };
    }, []);

    useQuery(EMAIL_ALL_QUERY, {
        variables: {
            path
        },
        skip: abort,
        onCompleted: (response) => {
            const { emailAll } = response || {};

            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'email_all_tracking',
                eventLabel: 'email_all_tracking',
                eventNonInteraction: true,
                taxonomy: snakeCaseKeys({
                    ...taxonomy,
                    emailAllFormShown: Boolean(emailAll),
                    emailAllTotalAgents: emailAll
                        ? emailAll.branches?.length
                        : 0
                })
            });

            if (emailAll) {
                setEmailAll({
                    form: emailAll,
                    result: null
                });
            }
        }
    });

    const [contactAgentsMutation] = useMutation(CONTACT_AGENTS, {
        onCompleted: (data) => {
            if (abort) return;
            setLoading(false);
            if (data.contactAgents.result) {
                setEmailAll({
                    form: null,
                    result: data.contactAgents.result
                });
            } else {
                [
                    'name' as const,
                    'email' as const,
                    'phone' as const,
                    'status' as const,
                    'message' as const
                ].forEach((fieldName) => {
                    const formField = getEmailFormField(
                        fieldName,
                        data.contactAgents?.emailAll
                    );
                    if (formField?.validationData?.requiredError) {
                        formHandlers.setError(fieldName, {
                            type: 'required',
                            message: requiredErrorMessages[fieldName]
                        });
                    }
                    if (formField?.validationData?.patternError) {
                        formHandlers.setError(fieldName, {
                            type: 'required',
                            message: requiredErrorMessages[fieldName]
                        });
                        formHandlers.setError(fieldName, {
                            type: 'pattern',
                            message: patternErrorMessages[fieldName]
                        });
                    }
                });

                if (
                    data?.contactAgents?.emailAll?.branchesValidation
                        ?.requiredError
                ) {
                    formHandlers.setError('branches', {
                        type: 'required',
                        message: 'Please select at least 1 agent or developer'
                    });
                }
            }
        },
        onError: () => {
            if (abort) return;
            setFormError(true);
            setLoading(false);
        }
    });

    const onSubmit = ({ getFreeValuation, ...data }: FormValues) => {
        setLoading(true);
        setFormError(false);

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'click',
            eventLabel: 'send_selected_agents'
        });

        const input = {
            ...data,
            branches: data.branches.map((branchId) =>
                Number.parseInt(branchId.toString(), 0)
            ),
            uri: path
        };

        return contactAgentsMutation({
            variables: {
                input
            }
        }).then((response) => {
            if (response.data) {
                const leadIdentifier =
                    response?.data?.contactAgents?.result?.leadIdentifier?.toString();

                if (leadIdentifier) {
                    const products =
                        response.data.contactAgents.result.branches.map(
                            ({
                                branchId,
                                companyName,
                                companyId
                            }: {
                                branchId: number;
                                companyId: number;
                                companyName: string;
                            }) => ({
                                product_id: branchId.toString(),
                                branch_id: branchId.toString(),
                                branch_name: companyName,
                                company_id: companyId.toString()
                            })
                        );

                    trackEvent(
                        'Applicant Lead Submit',
                        // TODO: Remove this type casting. Type "Taxonomy" from
                        // the tracking lib is much more constrained than
                        // "AnalyticsTaxonomy". These should be aligned so that
                        // they can narrow to the same type and can be used
                        // for tracking events
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        taxonomy as unknown as Taxonomy,
                        {
                            leadCategory: searchType,
                            leadVariant: data.status,
                            leadType: 'applicant',
                            leadFocus: 'batch',
                            leadMedium: 'email',
                            leadValuationToggle: getFreeValuation,
                            leadId: leadIdentifier,
                            memberType: 'developer',
                            products: products
                        }
                    );
                }
            }
        });
    };

    return {
        emailAll,
        formHandlers,
        onSubmit,
        isError,
        isLoading,
        eventCategory
    };
};

export default useEmailAll;
